import { ErrorBoundary, Shadow, Text, VStack } from '@genial/components';
import { BoxRadius, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Box, Center, Spinner } from 'native-base';
import React, { ReactNode, useEffect } from 'react';
import { useWindowDimensions } from 'react-native';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { FilterModal } from '../../../../../../../components/FilterModal';
import { useRecoilQuery } from '../../../../../../../hooks/useRecoilQuery';
import { SignedInNavigationScreenNavigationProp } from '../../../../../../../routes/types';
import { investmentStrategyFilterPositiveAtom } from '../../../../../../../store/home/conversion/atom';
import {
  investmentStrategyCustomerSelector,
  investmentStrategyPositiveProductClassesSelector,
} from '../../../../../../../store/home/conversion/selector';
import { masterStateSelector } from '../../../../../../../store/master/selector';
import { sessionState } from '../../../../../../../store/session/atom';

const Wrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { height } = useWindowDimensions();
  const backgroundColor = Utils.ColorToken.WithOpacity('background', 0.9);

  return (
    <Box flex={1} backgroundColor={backgroundColor} height={height}>
      <Center flex={1}>
        <Shadow level={1} paddingX={Spacing['inset.lg']} borderRadius={BoxRadius.sm}>
          {children}
        </Shadow>
      </Center>
    </Box>
  );
};

export const InvestmentStrategyFilter: React.FC = () => {
  const { userProfile, isMaster } = useRecoilValue(sessionState);
  const {
    data: masterStateData,
    loading: masterStateLoading,
    error: masterStateError,
    refresher: masterStateRefresher,
  } = useRecoilQuery(masterStateSelector({ isMaster }));
  const {
    data: productClasses,
    loading: productClassesLoading,
    error: productClassesError,
    refresher: productClassesRefresher,
  } = useRecoilQuery(investmentStrategyPositiveProductClassesSelector);
  const currentFilterOptions = useRecoilValue(investmentStrategyFilterPositiveAtom);
  const setFilterOptions = useSetRecoilState(investmentStrategyFilterPositiveAtom);
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { data: customerList } = useRecoilQuery(
    investmentStrategyCustomerSelector({
      kind: 'positive',
      searchTerm: currentFilterOptions.selectedFilters[1]?.filter as string,
    }),
    { if: () => !!currentFilterOptions.selectedFilters[1]?.filter }
  );

  const handleDismiss = () => {
    navigation.goBack();
  };

  const errorMessage = 'Erro ao carregar filtros';

  useEffect(() => {
    if (masterStateData && productClasses) {
      const customerListNormalized =
        customerList?.data?.map((customer) => ({
          label: customer,
          value: customer,
        })) ?? [];

      setFilterOptions((filterOptions) => ({
        ...filterOptions,
        availableFilters: [
          { type: 'date', fieldName: 'date', title: 'Data', placeholder: 'Selecione uma data' },
          {
            type: 'autocomplete',
            title: 'Cliente',
            fieldName: 'customerName',
            options: customerListNormalized,
            customAutocompleteProps: {
              maxOptionPerRender: 10,
              placeholder: 'Pesquise por nome na ordem correta',
            },
          },
          {
            type: 'select',
            title: 'Classe produto',
            options: productClasses,
            fieldName: 'productClass',
          },
        ],
      }));
    }
  }, [customerList, masterStateData, productClasses, setFilterOptions, userProfile?.idParceiro]);

  if (masterStateError) {
    return (
      <Wrapper>
        <ErrorBoundary
          errorMessage={errorMessage}
          onCancel={handleDismiss}
          onRefresh={masterStateRefresher}
        />
      </Wrapper>
    );
  }

  if (productClassesError) {
    return (
      <Wrapper>
        <ErrorBoundary
          errorMessage={errorMessage}
          onCancel={handleDismiss}
          onRefresh={productClassesRefresher}
        />
      </Wrapper>
    );
  }

  if (masterStateLoading || productClassesLoading) {
    return (
      <Wrapper>
        <VStack space={Spacing['stack.xxxs']} marginY={Spacing['inline.xxxs']}>
          <Spinner size="lg" />
          <Text>Filtros carregando</Text>
        </VStack>
      </Wrapper>
    );
  }

  return <FilterModal onClose={handleDismiss} filterAtom={investmentStrategyFilterPositiveAtom} />;
};
